/**
 * Toucan People — handing someone to the phone's own address book, as the
 * platform's contact card. Android downloads it from the server's address,
 * a link the form renders; everywhere else the card is shared where
 * the share sheet offers Contacts, and downloaded otherwise, which a phone
 * opens in Contacts and a desktop keeps.
 */

import { contactCardFileName, contactCardPath, vcard } from "toucan";
import type { ContactCard } from "toucan";
import type { Person } from "./model.ts";

/** What of a person goes on their card. */
export function card(person: Person): ContactCard {
  const made: ContactCard = { name: person.name };
  if (person.phone !== undefined) {
    made.phone = person.phone;
  }
  if (person.note !== undefined) {
    made.note = person.note;
  }
  return made;
}

/** The address, under this app, that downloads somebody's card. */
export function contactLink(person: Person): string {
  return contactCardPath(card(person));
}

/** Whether this is an Android phone, where only a download reaches Contacts. */
export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

/**
 * Give a phone that is not Android this person as a contact card. The share
 * sheet is the way that ends in the address book in one tap, so it is taken
 * wherever it accepts a file. Otherwise the card is downloaded instead.
 */
export async function saveToContacts(person: Person): Promise<void> {
  const made = card(person);
  const file = new File([vcard(made)], contactCardFileName(made), {
    type: "text/vcard",
  });
  if (
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({ files: [file], title: person.name });
    } catch {
      // The sheet was dismissed, which is an answer rather than a failure.
    }
    return;
  }
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  link.click();
  // A browser may start the download a beat after the click, so the address
  // has to outlive this call.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}
